/**
 * Sales Dashboard Types
 * Types for the consolidated gym-style sales dashboard
 */

export type DashboardPeriod = 'today' | 'week' | 'month';

// KPI Section
export interface SalesKPIs {
  monthlyTarget: number;
  monthlyAchieved: number;
  totalSales: number;
  netRevenue: number;
  activePipeline: number;
}

// Performance Section
export interface SalesPerformance {
  salesPeriod: number;
  periodTarget: number;
  targetAchieved: number;
  remainingTarget: number;
  dealsWon: number;
  dealsWonMonth: number;
  pipelineValue: number;
}

// Target Progress Section
export interface TargetProgress {
  percentage: number;
  achieved: number;
  remaining: number;
  daysLeft: number;
  target: number;
  targetsMissing?: boolean;
}

// Payment Metrics
export interface PaymentMetric {
  count: number;
  amount: number;
}

export interface PaymentsByMethod {
  online: PaymentMetric;
  cash: PaymentMetric;
}

export interface PaymentStatus {
  received: PaymentMetric;
  pending: PaymentMetric;
  overdue: PaymentMetric;
  byMethod: PaymentsByMethod;
  totalReceived: number;
  totalReceivedCount: number;
}

// Pipeline Health
export interface StageData {
  stage: string;
  count: number;
  value: number;
}

export interface ExpectedClosures {
  count: number;
  value: number;
}

export interface PriorityBreakdown {
  HOT: number;
  WARM: number;
  COLD: number;
}

export interface PipelineHealth {
  byStage: StageData[];
  expectedWeek: ExpectedClosures;
  expectedMonth: ExpectedClosures;
  byPriority: PriorityBreakdown;
}

// Executive Performance
export interface ExecutiveData {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  activeLeads: number;
  followUpsDone: number;
  proposalsSent: number;
  dealsWon: number;
  dealsWonMonth: number;
  revenueAchieved: number;
  revenuePeriod: number;
  // null when the executive has no target set for the current month.
  // Frontend should render "—" / "No target" instead of "0%".
  targetProgress: number | null;
  targetValue: number;
  paymentsReceived: number;
  paymentsCount: number;
  rank: number;
}

// Complete Dashboard Data
export interface SalesDashboardData {
  kpis: SalesKPIs;
  performance: SalesPerformance;
  targetProgress: TargetProgress;
  payments: PaymentStatus;
  pipeline: PipelineHealth;
  executives: ExecutiveData[];
}

// API Response
export interface SalesDashboardResponse {
  success: boolean;
  data: SalesDashboardData;
  meta: {
    period: DashboardPeriod;
    role: string;
    userId: string;
    timestamp: string;
  };
}

// Quick Stats
export interface QuickStats {
  todayRevenue: number;
  todayDeals: number;
  monthRevenue: number;
  monthDeals: number;
  activePipeline: number;
  pipelineValue: number;
}

export interface QuickStatsResponse {
  success: boolean;
  data: QuickStats;
  meta: {
    timestamp: string;
  };
}

// Period Options
export const PERIOD_OPTIONS: { value: DashboardPeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

// Stage Display Names
export const STAGE_LABELS: Record<string, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  DISCUSSION: 'Discussion',
  PROPOSAL: 'Proposal',
  NEGOTIATION: 'Negotiation',
  WON: 'Won',
  LOST: 'Lost',
};

// KPI Card Config
export interface KPICardConfig {
  key: keyof SalesKPIs;
  label: string;
  icon: string;
  gradient: string;
  textColor: string;
  format: 'currency' | 'number';
}

export const KPI_CARD_CONFIGS: KPICardConfig[] = [
  {
    key: 'monthlyTarget',
    label: 'Monthly Target',
    icon: 'Target',
    gradient: 'from-red-500 to-red-600',
    textColor: 'text-white',
    format: 'currency',
  },
  {
    key: 'totalSales',
    label: 'Total Sales',
    icon: 'TrendingUp',
    gradient: 'from-orange-500 to-orange-600',
    textColor: 'text-white',
    format: 'currency',
  },
  {
    key: 'netRevenue',
    label: 'Net Revenue',
    icon: 'IndianRupee',
    gradient: 'from-green-500 to-green-600',
    textColor: 'text-white',
    format: 'currency',
  },
  {
    key: 'activePipeline',
    label: 'Active Pipeline',
    icon: 'Users',
    gradient: 'from-yellow-500 to-yellow-600',
    textColor: 'text-white',
    format: 'number',
  },
];

// Payment Status Colors
export const PAYMENT_STATUS_COLORS = {
  received: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: 'text-green-600',
  },
  pending: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    icon: 'text-yellow-600',
  },
  overdue: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: 'text-red-600',
  },
};

// Priority Colors
export const PRIORITY_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  HOT: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  WARM: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
  COLD: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
};
